import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, FileText, Lock, Edit, Calendar, MessageCircle, CreditCard, Shield, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { faqPageSchema } from '../utils/structuredData';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './FAQs.css';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqCategories = [
    {
      icon: <Calendar size={28} strokeWidth={1.5} />,
      title: 'Booking & Appointments',
      faqs: [
        {
          question: 'How do I book?',
          answer: (
            <>
              You can contact us via phone (<a href="tel:0266002710" className="inline-link">02 6600 2710</a>), SMS (<a href="sms:0415220944" className="inline-link">0415 220 944</a>), email (<a href="mailto:contact@clinicalminds.net" className="inline-link">contact@clinicalminds.net</a>) or by using our <Link to="/contact?enquiry=Assessment" className="inline-link">contact form</Link>. You can also visit our <Link to="/bookings" className="inline-link">bookings page</Link> for more details. If this is your first appointment, we will call you within 24 hours to ensure we are the right service for you, and then will book you in.
            </>
          )
        },
        {
          question: 'How do I schedule a follow up appointment?',
          answer: 'We can schedule your appointment after your session, together, or you can phone, email or text us and we can arrange it for you.'
        },
        {
          question: 'What happens after my first appointment is booked in?',
          answer: 'You will be emailed intake forms and other important information to review. These forms can be completed online and should be completed prior to our appointment, so we can be fully prepared and make our time together efficient. However, if you do have questions, we can do the forms together, during the session (just note, it may take up to 20 minutes to complete). Importantly, the forms do need to be finished before we start our first session.'
        },
        {
          question: 'Will I receive reminders?',
          answer: 'You will be sent an SMS and an email 3 days prior to your appointment to remind you of the time and date. If you can respond to the SMS to confirm the booking, that would be great.'
        },
        {
          question: 'Can I change my booking?',
          answer: 'Yes, you can either SMS, call or email us if you need to make a change. We will get back to you as soon as we can to help accommodate your needs. We just need at least 24 hours notice.'
        },
        {
          question: 'What if I need to cancel or reschedule?',
          answer: (
            <>
              24 hours' notice is required for cancellations to allow the time slot to be offered to other clients. If you cancel within 24 hours, you will be charged the full fee. You can read our <Link to="/terms-and-conditions" className="inline-link">terms and conditions</Link> that goes into detail about our cancellation policy.
            </>
          )
        },
        {
          question: 'What\'s the difference between face-to-face and telehealth?',
          answer: 'Face-to-face sessions are at the Coffs Harbour practice, while telehealth sessions are conducted via a secure video call from your own space.'
        }
      ]
    },
    {
      icon: <Sparkles size={28} strokeWidth={1.5} />,
      title: 'Getting Started',
      faqs: [
        {
          question: 'Can all of my bookings and communication with your practice manager be via email or SMS?',
          answer: 'We try to make sure all your interactions can be online, because we know you are busy. You can respond to your reminder SMSs if you need to change appointments, your forms will be sent to you digitally. No paper, no printing, no phone calls unless you need to!'
        },
        {
          question: 'What should I expect in my first session?',
          answer: 'The first session focuses on understanding your concerns, goals, and developing a treatment plan together.'
        },
        {
          question: 'I don\'t know if therapy is right for me!',
          answer: 'There is never an obligation to continue once you start. If it doesn\'t feel right, we can work together to find someone else or determine if it\'s not what you\'re looking for.'
        },
        {
          question: 'How do I prepare for my first session?',
          answer: 'Complete any intake forms sent to you beforehand. Think about what you\'d like to discuss and any questions you have. Arrive 10-15 minutes early for face-to-face sessions. For telehealth, make sure you have a private, quiet space with a good internet connection.'
        },
        {
          question: 'What sort of paperwork do I need to fill out?',
          answer: (
            <>
              You'll be emailed various forms after your first appointment is booked in. These forms help us understand more about you and they cover important topics like privacy and consent. Consent is really important to us, as we want to make sure you understand the process of psychology, what data we keep, what we can and can't disclose and how we treat your confidential information. You can read more within our <Link to="/privacy-policy" className="inline-link">privacy policy here</Link>.
            </>
          )
        }
      ]
    },
    {
      icon: <MessageCircle size={28} strokeWidth={1.5} />,
      title: 'Sessions & Treatment',
      faqs: [
        {
          question: 'How long are sessions and how often should I attend?',
          answer: 'Sessions are typically 50 minutes long. The frequency of sessions is totally up to you!'
        },
        {
          question: 'Is everything I share confidential?',
          answer: (
            <>
              Yes, everything discussed in sessions is confidential. However, there are limited circumstances where confidentiality may be need to be broken, which will be explained during your first meeting and you can also read about in our <Link to="/privacy-policy" className="inline-link">privacy policy</Link>. All of your data is kept securely, in line with Australian privacy laws and requirements. To ensure you fully understand how confidentiality works and any limits to this, you will be emailed a form called "informed consent" and this is something we take very seriously. We will talk you through this at your first appointment, to make sure you understand it. Consent is something we will re-visit at least annually or at anytime you wish.
            </>
          )
        },
        {
          question: 'What qualifications does Lachlan have?',
          answer: 'Lachlan holds a Masters of Psychology (Clinical), Bachelor of Psychological Science (Honours), Graduate Diploma of Psychological Science, and a Bachelor of Commerce with a Diploma of Arts (Political Science), CPA. He is a registered psychologist with the Australian Health Practitioner Regulation Agency (AHPRA), PSY0002785698, and a Clinical Psychology Registrar.'
        },
        {
          question: 'What types of therapy do you offer?',
          answer: 'The practice offers several evidence-based therapeutic approaches tailored to your needs, including: Schema Therapy, Dialectical Behaviour Therapy (DBT), Motivational Interviewing, Cognitive Behavioural Therapy (CBT), Mindfulness Based Interventions, Acceptance and Commitment Therapy (ACT), and Trauma Informed CBT.'
        },
        {
          question: 'Do you offer telehealth sessions?',
          answer: 'Yes, secure telehealth sessions are offered via video call. Telehealth appointments are booked during business hours. You\'ll need a stable internet connection and a private space.'
        },
        {
          question: 'How do I book an appointment?',
          answer: (
            <>
              You can request an appointment through the <Link to="/bookings" className="inline-link">booking page</Link>, or contact the practice directly at <a href="tel:0266002710" className="inline-link">02 6600 2710</a>, <a href="sms:0415220944" className="inline-link">0415 220 944 (SMS)</a> or <a href="mailto:contact@clinicalminds.net" className="inline-link">contact@clinicalminds.net</a>.
            </>
          )
        }
      ]
    },
    {
      icon: <CreditCard size={28} strokeWidth={1.5} />,
      title: 'Fees',
      faqs: [
        {
          question: 'What are your session fees?',
          answer: 'Individual therapy sessions are $220 for a standard 50-minute session. With a Mental Health Treatment Plan from your GP, you can claim a Medicare rebate of $101.55 per session. Payment can be made online with a credit card or in person with EFTPOS. Psychological Assessment fees (ADHD, Autism Spectrum Disorder, Learning Difficulties, Cognitive Testing) will be discussed prior to you booking, as it will depend on your needs. There is generally no Medicare rebate for psychological assessments, though in some cases a paediatrician can refer for a complex neurodevelopmental assessment where some rebates apply, and NDIS funding may be available.'
        },
        {
          question: 'How are the Medicare Rebates paid?',
          answer: 'Medicare rebates are deposited into your Medicare nominated bank account (over night) if you are paying online or via credit card with our EFTPOS machine. However, if you have an EFTPOS debit card, we can credit the rebate immediately into your bank account.'
        }
      ]
    },
    {
      icon: <FileText size={28} strokeWidth={1.5} />,
      title: 'Referrals (Mental Health Treatment Plan)',
      faqs: [
        {
          question: 'What is a Mental Health Treatment Plan?',
          answer: 'A Mental Health Treatment Plan is a document created by a general practitioner (GP) in Australia that outlines a patient\'s mental health needs, goals, and a treatment plan. It allows eligible patients to access Medicare rebates for up to 10 individual psychological sessions per calendar year. This plan acts as a referral, ensuring that you can receive subsidised sessions with a registered psychologist. The primary purpose of the plan is to make mental health support more affordable and accessible for Australians.'
        },
        {
          question: 'How do I get a Mental Health Treatment Plan?',
          answer: 'Your GP can help you with this. If you would like to access psychology with a Mental Health Treatment Plan, you may wish visit your GP and talk to them about this. Your GP will assess your needs and, if appropriate, provide a Mental Health Treatment Plan. This plan allows you to claim Medicare rebates for up to 10 sessions per calendar year. You need this plan to be eligible for the medicare rebate.'
        },
        {
          question: 'Do I need to have a copy of the Mental Health Treatment Plan and/or send it to you?',
          answer: 'Your GP usually sends us a copy of the plan, but if we don\'t receive it, we may ask you to follow this up with them.'
        },
        {
          question: 'Does the Mental Health Treatment Plan need to be reviewed during the year?',
          answer: 'Yes, after your first 6 sessions, I will write to your GP, updating them on your progress. You will then need to book an appointment to see them, so they can request your final 4 appointments for the year.'
        },
        {
          question: 'Do you accept health insurance?',
          answer: 'Yes, private health insurance claims can be made online after your appointment, so long as your cover extends to Psychology. The practice also accepts NDIS funding and can invoice this on your behalf.'
        }
      ]
    },
    {
      icon: <Shield size={28} strokeWidth={1.5} />,
      title: 'Privacy',
      faqs: [
        {
          question: 'Is everything I share in sessions confidential?',
          answer: (
            <>
              Yes, everything discussed in sessions is confidential. However, there are limited circumstances where confidentiality may be need to be broken, which will be explained during your first meeting and you can also read about in our <Link to="/privacy-policy" className="inline-link">privacy policy</Link>. All of your data is kept securely, in line with Australian privacy laws and requirements. To ensure you fully understand how confidentiality works and any limits to this, you will be emailed a form called "informed consent" and this is something we take very seriously. We will talk you through this at your first appointment, to make sure you understand it. Consent is something we will re-visit at least annually or at anytime you wish.
            </>
          )
        },
        {
          question: 'How is my personal information stored and protected?',
          answer: (
            <>
              All client records are stored securely using encrypted digital systems that comply with Australian privacy legislation. Physical documents are kept in locked filing cabinets in a secure location. Access to your information is restricted to authorised personnel only. You can read more about this in our <Link to="/privacy-policy" className="inline-link">privacy policy here</Link>.
            </>
          )
        },
        {
          question: 'Who has access to my personal information?',
          answer: 'Only your psychologist and essential administrative staff have access to your personal information. Information is never shared with third parties without your explicit written consent, except in very limited legal circumstances that will be explained to you.'
        },
        {
          question: 'Can I access my own records?',
          answer: 'Yes, you have the right to access your own psychological records. You can request to view or obtain copies of your records by contacting the practice. There may be a small administrative fee for copying services.'
        },
        {
          question: 'How long are my records kept?',
          answer: 'Client records are retained for a minimum of 7 years after your last session, as required by professional guidelines and Australian law. After this period, records are securely destroyed.'
        },
        {
          question: 'What happens to my information if I want to transfer to another psychologist?',
          answer: 'With your written consent, we can transfer relevant information to your new psychologist to ensure continuity of care. You have full control over what information is shared and with whom.'
        }
      ]
    }
  ];

  // FAQ structured data, built from the answers exactly as shown on the page
  const faqSchema = faqPageSchema(faqCategories.flatMap((c) => c.faqs));

  const toggleFAQ = (categoryIndex, faqIndex) => {
    const uniqueIndex = `${categoryIndex}-${faqIndex}`;
    setOpenIndex(openIndex === uniqueIndex ? null : uniqueIndex);
  };

  return (
    <div className="page-faqs">
      <SEO 
        title="FAQs | Psychology Questions Answered | Clinical Minds Psychology"
        description="Frequently asked questions about psychology services, Medicare rebates, booking, fees and what to expect at Clinical Minds Psychology."
        canonicalUrl="https://www.clinicalminds.net/faqs"
        schemaData={faqSchema}
      />
      
      {/* Scroll Indicator for Mobile */}
      <ScrollIndicator />
      
      {/* Hero Section */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">FAQ</span>
          <h1 className="heading-hero">Frequently Asked Questions</h1>
        </div>
      </section>

      <section className="faqs-content">
        <div className="container">
          {/* Key Forms Section */}
          <div className="forms-section">
            <div className="forms-card card">
              <div className="forms-header">
                <FileText size={28} strokeWidth={1.5} className="forms-icon" />
                <h2 className="heading-2">Important Forms & Documents</h2>
              </div>
              <div className="forms-grid">
                <a 
                  href="/Clinical_Minds_Privacy_Policy.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="form-link"
                >
                  <div className="form-item">
                    <Lock size={20} strokeWidth={1.5} className="form-icon" />
                    <div className="form-content">
                      <h3 className="heading-3">Privacy Policy</h3>
                      <p className="body-small">Review how we protect and handle your personal information</p>
                    </div>
                  </div>
                </a>
                
                <a 
                  href="/Clinical_Minds_Terms_and_Conditions.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="form-link"
                >
                  <div className="form-item">
                    <FileText size={20} strokeWidth={1.5} className="form-icon" />
                    <div className="form-content">
                      <h3 className="heading-3">Terms and Conditions</h3>
                      <p className="body-small">Important terms for using our services and booking system</p>
                    </div>
                  </div>
                </a>
                
                <a 
                  href="/Clinical_Minds_New_Client_Intake_and_Consent.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="form-link"
                >
                  <div className="form-item">
                    <Edit size={20} strokeWidth={1.5} className="form-icon" />
                    <div className="form-content">
                      <h3 className="heading-3">New Client Intake Form</h3>
                      <p className="body-small">Download if you can't fill the form out online</p>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* FAQ Introduction Section */}
          <div className="faq-intro-section">
            <div className="faq-intro-card card">
              <div className="faq-intro-header">
                <MessageCircle size={28} strokeWidth={1.5} className="faq-intro-icon" />
                <h2 className="heading-2">Frequently Asked Questions</h2>
              </div>
              <p className="body-medium faq-intro-description">
                Find answers to the most common questions about our services, booking process, and policies. Click on any question to reveal detailed information.
              </p>
            </div>
          </div>

          {faqCategories.map((category, categoryIndex) => {
            // Generate ID from category title
            const categoryId = category.title.toLowerCase().replace(/ /g, '-').replace(/[()&]/g, '');
            
            return (
              <div key={categoryIndex} id={categoryId} className="faq-category">
                <div className="category-header">
                  <span className="category-icon">{category.icon}</span>
                  <h2 className="heading-1 category-title">{category.title}</h2>
                </div>
                <div className="category-faqs">
                  {category.faqs.map((faq, faqIndex) => {
                    const uniqueIndex = `${categoryIndex}-${faqIndex}`;
                    return (
                      <div key={faqIndex} className="faq-item card">
                        <button
                          className="faq-question"
                          onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                          aria-expanded={openIndex === uniqueIndex}
                        >
                          <h3 className="heading-3">{faq.question}</h3>
                          {openIndex === uniqueIndex ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                        <div className="faq-answer" hidden={openIndex !== uniqueIndex}>
                          <p className="body-medium">{faq.answer}</p>
                        </div>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}

          <div className="faqs-cta card">
            <h2 className="heading-2">Still Have Questions?</h2>
            <p className="body-medium">
              Can't find the answer you're looking for? I'm here to help. Reach out directly, and I'll be happy to assist you.
            </p>
            <a href="/contact" className="btn-primary">Contact Us</a>
          </div>
        </div>
      </section>

      {/* Crisis Support Section */}
      <CrisisSupport />
    </div>
  );
};

export default FAQs;
